import { useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { LegalEntityModel, PersonModel } from 'app/shared/oib/oib.model';
import { SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import OibInput from 'app/shared/oib/oib-input';
import AddressInput from 'app/shared/address-input/address-input';
import React from 'react';
import { siteConstructorInitialData } from 'app/edozvola/cases/construction-participants/model/construction-participants.model';
import {
  fetchSiteConstructorQuery,
  saveSiteConstructorQuery,
} from 'app/edozvola/cases/construction-participants/service/construction-participants.queries';
import { siteConstructorValidationSchema } from 'app/edozvola/cases/construction-participants/model/construction-participants.schema';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { fetchSubjectTypeQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { formatAddress } from 'app/shared/address-input/address-helper';

export interface ISiteConstructorFormProps {
  idSiteConstructor: string | null;
  setIdSiteConstructor: any;
  readOnly: boolean;
}

const SiteConstructorForm = (props: ISiteConstructorFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: siteConstructorData, isLoading: siteConstructorDataLoading } = fetchSiteConstructorQuery(props.idSiteConstructor);
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();

  const formik = useFormik({
    initialValues: siteConstructorData || siteConstructorInitialData,
    onSubmit: values => saveSiteConstructorMutation(values),
    validationSchema: siteConstructorValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    initialTouched: {
      person: {
        oib: !siteConstructorData,
      },
      legalEntity: {
        oib: !siteConstructorData,
      },
      address: {
        cityMunicipality: true,
        community: true,
        postalCode: true,
        street: true,
      },
    },
  });

  const { mutate: saveSiteConstructorMutation, isPending: isSiteConstructorSaving } = saveSiteConstructorQuery(uuid, () => closeModal());

  const filteredSubjectTypeOptions = React.useMemo(() => {
    return subjectTypeOptions?.filter((opt: any) => opt.id !== SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST && opt.id !== SUBJECT_TYPE_ENUM.OPG);
  }, [subjectTypeOptions]);

  const oibFetched = (fetchedOibData: PersonModel | LegalEntityModel) => {
    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA)) {
      formik.setFieldValue('person', fetchedOibData);
    } else {
      formik.setFieldValue('legalEntity', fetchedOibData);
    }
  };

  const onOibChanged = (value: string) => {
    formik.setFieldValue('person.address', null);
    formik.setFieldValue('person.firstName', '');
    formik.setFieldValue('person.lastName', '');
    formik.setFieldValue('legalEntity.companyName', '');
    formik.setFieldValue('legalEntity.address', null);

    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)) {
      formik.setTouched({
        ...formik.touched,
        legalEntity: {
          oib: true,
        },
      });
      formik.setFieldValue('legalEntity.oib', value);
    } else {
      formik.setTouched({
        ...formik.touched,
        person: {
          oib: true,
        },
      });
      formik.setFieldValue('person.oib', value);
    }
  };

  const onSubjectTypeChange = selected => {
    onOibChanged('');
    formik.setFieldValue('legalEntity.mb', '');
    formik.setFieldValue('subjectType', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0].id === subjectType;

  const isPravnaIliFizickaOsobaSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA);
  const isObrtSelected = isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT);

  const closeModal = () => {
    props.setIdSiteConstructor(null);
    formik.resetForm({ values: siteConstructorInitialData });
  };

  const phoneChanged = (event: any, field: string) => {
    formik.setFieldValue(field, event.target.value);
    event.preventDefault();
  };

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.idSiteConstructor !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled izvođača</ModalHeader>
        ) : (
          <ModalHeader>{props.idSiteConstructor === '' ? 'Unos izvođača' : 'Uređivanje izvođača'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {siteConstructorDataLoading && <Loader />}
            {!siteConstructorDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu subjekta'}
                      onItemSelect={onSubjectTypeChange}
                      selected={formik.values.subjectType}
                      labelKey={'value'}
                      options={filteredSubjectTypeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.subjectType as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    {isPravnaIliFizickaOsobaSelected ? (
                      <OibInput
                        value={
                          isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.values.legalEntity?.oib : formik.values.person?.oib
                        }
                        handleBlur={formik.handleBlur}
                        handleChange={event => onOibChanged(event.target.value)}
                        errors={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.errors.legalEntity : formik.errors.person}
                        touched={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.touched.legalEntity : formik.touched.person}
                        onOibFetched={oibFetched}
                        fetchLegalEntity={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        disabled={props.readOnly || !formik.values.subjectType || formik.values.subjectType.length === 0}
                      />
                    ) : (
                      isObrtSelected && (
                        <>
                          <Label labelText="MB" />
                          <Input
                            value={formik.values.legalEntity?.mb}
                            id="mb"
                            name="mb"
                            maxLength={11}
                            disabled={props.readOnly}
                            onChange={event => formik.setFieldValue('legalEntity.mb', event.target.value)}
                            error={formik.errors.legalEntity && formik.errors.legalEntity['mb'] ? formik.errors.legalEntity['mb'] : ''}
                          />
                        </>
                      )
                    )}
                  </Col>
                </Row>
                {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Ime'} />
                      <Input
                        disabled
                        value={formik.values.person.firstName}
                        error={formik.errors.person && formik.errors.person['firstName'] ? formik.errors.person['firstName'] : ''}
                        onChange={event => formik.setFieldValue('person.firstName', event.target.value)}
                      />
                    </Col>
                    <Col md={6}>
                      <Label labelText={'Prezime'} />
                      <Input
                        disabled
                        value={formik.values.person.lastName}
                        error={formik.errors.person && formik.errors.person['lastName'] ? formik.errors.person['lastName'] : ''}
                        onChange={event => formik.setFieldValue('person.lastName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {(isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isObrtSelected) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Naziv'} />
                      <Input
                        disabled={props.readOnly || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        value={formik.values.legalEntity.companyName}
                        error={
                          formik.errors.legalEntity && formik.errors.legalEntity['companyName']
                            ? formik.errors.legalEntity['companyName']
                            : ''
                        }
                        maxLength={200}
                        onChange={event => formik.setFieldValue('legalEntity.companyName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {isPravnaIliFizickaOsobaSelected && formik.values.person?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.person.address)}</span>
                    </Col>
                  </Row>
                )}

                {isPravnaIliFizickaOsobaSelected && formik.values.legalEntity?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.legalEntity.address)}</span>
                    </Col>
                  </Row>
                )}
                <AddressInput
                  field={formik.values.address}
                  errors={formik.errors.address}
                  readOnly={props.readOnly}
                  setFieldValue={(field: string, value: any) => formik.setFieldValue(`address.${field}`, value)}
                />
                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input
                      value={formik.values.email}
                      id={'email'}
                      name={'email'}
                      error={formik.errors.email ?? ''}
                      disabled={props.readOnly}
                      maxLength={60}
                      onChange={event => formik.setFieldValue(`email`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input
                      value={formik.values.telephoneNumber}
                      id={'telephoneNumber'}
                      name={'telephoneNumber'}
                      disabled={props.readOnly}
                      maxLength={50}
                      onChange={e => phoneChanged(e, 'telephoneNumber')}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={isSiteConstructorSaving}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SiteConstructorForm;
